import { useMemo, useRef } from "react";
import { CALLBACK_KEYS } from "@elevenlabs/client/internal";
/**
 * Wraps user-provided callback props in stable ref-backed functions,
 * preventing stale closure bugs when the session outlives renders.
 *
 * Returns a `Partial<Callbacks>` containing only the keys the caller
 * actually provided. Function references are stable per key across
 * renders, but always invoke the latest prop value. The returned object
 * reference is stable as long as the set of provided keys doesn't change.
 */
export function useStableCallbacks(props) {
    // Store the latest prop value for each callback in a ref.
    // Uses Record<string, unknown> to avoid TypeScript's union-to-intersection
    // issue when indexing Callbacks with a union of all its keys.
    const callbackRefs = useRef({});
    // Compute a stable scalar from the set of provided keys so we can
    // memoize the result object.
    const activeKeys = CALLBACK_KEYS.filter(key => props[key] !== undefined);
    const activeKeySet = activeKeys.join("|");
    const stableCallbacks = useMemo(() => Object.fromEntries(activeKeys.map(key => [
        key,
        (...args) => {
            const fn = callbackRefs.current[key];
            fn?.(...args);
        },
    ])), 
    // eslint-disable-next-line react-hooks/exhaustive-deps -- activeKeySet is a stable scalar derived from activeKeys
    [activeKeySet]);
    for (const key of CALLBACK_KEYS) {
        // eslint-disable-next-line react-hooks/refs -- callback refs intentionally track latest props for stable event handlers
        callbackRefs.current[key] = props[key];
    }
    return stableCallbacks;
}
//# sourceMappingURL=useStableCallbacks.js.map